import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PS } from '../theme';
import { Avatar, Chip, CreditMeter, VerifiedMark, DemoBadge, VisibilityBadge } from '../lib/ui';
import { Icon } from '../lib/icons';
import { thb } from '../lib/format';
import Screen from '../components/Screen';
import JoinSheet from '../components/JoinSheet';
import ShareSheet from '../components/ShareSheet';
import ReviewSheet from '../components/ReviewSheet';
import DokCalculator from '../components/DokCalculator';
import CircleSchedule from '../components/CircleSchedule';
import CircleReviews from '../components/CircleReviews';
import HandsBoard from '../components/HandsBoard';
import PersonSheet from '../components/PersonSheet';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../auth/AuthContext';
import api, { BACKEND_URL } from '../lib/api';
import { confirmToast, notifySuccess, notifyError } from '../lib/toast';

const TRUST = [
  { k: 'สัญญาเล่นแชร์', v: 'e-Signature · ETDA-compliant' },
  { k: 'หลักฐานทุก event', v: 'SHA-256 + ลายเซ็นดิจิทัล แก้ไม่ได้' },
  { k: 'อยู่ในเกณฑ์ พ.ร.บ.', v: '≤ 30 คน · ≤ 300,000 บาท' },
];

export default function CircleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, promptLogin } = useAuth();
  const { data: c, loading, error, reload } = useFetch(`/circles/${id}`, [id]);
  const { data: rounds } = useFetch(`/circles/${id}/rounds`, [id]);
  const { data: reviews, reload: reloadReviews } = useFetch(`/circles/${id}/reviews`, [id]);
  const [showJoin, setShowJoin] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [person, setPerson] = useState(null);

  if (!c) {
    if (loading) return <Screen><div className="ps-live-dot" style={{ padding: 40, color: PS.textMuted }}>กำลังโหลด…</div></Screen>;
    return (
      <Screen bg={PS.page}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 32, textAlign: 'center' }}>
          <Icon.info size={34} stroke={PS.textMuted} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>{error?.response?.status === 404 ? 'ไม่พบวงแชร์นี้' : 'โหลดข้อมูลวงไม่สำเร็จ'}</div>
          <div style={{ fontSize: 13, color: PS.textMuted, lineHeight: 1.5, maxWidth: 280 }}>{error?.response?.status === 404 ? 'วงนี้อาจถูกปิดหรือลิงก์ไม่ถูกต้อง' : 'การเชื่อมต่อมีปัญหา ลองโหลดใหม่อีกครั้ง'}</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            <button data-testid="detail-retry-btn" onClick={reload} className="ps-press" style={{ background: PS.ink, color: PS.page, border: 'none', borderRadius: 999, padding: '11px 22px', fontSize: 14, fontWeight: 600, fontFamily: PS.fontUI }}>ลองอีกครั้ง</button>
            <button data-testid="detail-error-back-btn" onClick={() => nav('/app/discover')} className="ps-press" style={{ background: 'transparent', color: PS.text, border: `1px solid ${PS.line}`, borderRadius: 999, padding: '11px 22px', fontSize: 14, fontWeight: 600, fontFamily: PS.fontUI }}>กลับ</button>
          </div>
        </div>
      </Screen>
    );
  }

  const remaining = c.members_cap - c.members;
  const gatedNav = (path, reason) => { if (user) nav(path); else promptLogin(reason); };
  const isHost = user && c.host_user_id === user.user_id;
  const canLeave = c.is_member && c.my_status !== 'pending' && !isHost;
  const canReview = c.is_member && c.my_status !== 'pending' && !isHost;
  const leave = () => {
    confirmToast('ยืนยันถอนตัวจากวงนี้?', {
      confirmLabel: 'ถอนตัว', description: 'การถอนตัวจะถูกบันทึกเป็นหลักฐาน',
      onConfirm: async () => {
        try { await api.post(`/circles/${id}/leave`); notifySuccess('ถอนตัวจากวงแล้ว'); reload(); }
        catch (e) { notifyError(e?.response?.data?.detail || 'เกิดข้อผิดพลาด'); }
      },
    });
  };

  return (
    <Screen bg={PS.page}>
      {/* Hero */}
      <div className="cd-hero" style={{ position: 'relative', background: PS.ink, color: PS.page, flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button data-testid="detail-back-btn" onClick={() => nav(-1)} className="ps-press" style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(245,241,234,0.14)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon.chevronL size={18} stroke={PS.page} />
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <div onClick={() => setShowShare(true)} className="ps-press" data-testid="detail-share-btn" style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(245,241,234,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.send size={15} stroke={PS.page} /></div>
            <div onClick={() => gatedNav(`/app/circle/${id}/vault`, 'ดูห้องนิรภัยหลักฐานต้องเข้าสู่ระบบก่อน')} className="ps-press" data-testid="detail-vault-btn" style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(245,241,234,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.shield size={16} stroke={PS.page} /></div>
          </div>
        </div>
        <div className="cd-hero-body">
          <div className="cd-hero-main">
            <div style={{ display: 'flex', gap: 8, marginBottom: 9, flexWrap: 'wrap' }}>
              {c.is_demo && <DemoBadge size="lg" />}
              <VisibilityBadge visibility={c.visibility} size="lg" onLight={false} />
              {c.verified && <Chip tone="ink" style={{ background: 'rgba(245,241,234,0.14)', color: PS.page, fontSize: 10.5 }}><VerifiedMark size={10} />ตรวจสอบโดย Peershare</Chip>}
              <Chip tone="ink" style={{ background: 'rgba(245,241,234,0.14)', color: PS.page, fontSize: 10.5 }}><Icon.pin size={10} stroke={PS.page} />{c.distance_km != null ? `${c.distance_km} กม.` : '—'}</Chip>
            </div>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.3, lineHeight: 1.15 }}>{c.name}</div>
            <div style={{ fontSize: 12.5, color: 'rgba(245,241,234,0.65)', marginTop: 5 }}>{c.area_name}{c.started_ago ? ` · เริ่ม ${c.started_ago}ที่แล้ว` : ''}</div>
          </div>
          <div className="cd-hero-stats" style={{ background: 'rgba(245,241,234,0.1)' }}>
            {[{ l: 'เงินต้น/งวด', v: thb(c.amount_per_round), sub: `${c.rounds_count} งวด` },
              { l: 'ยอดรวม', v: thb(c.total_amount), sub: '≤ 300,000฿' },
              { l: 'สมาชิก', v: `${c.members}/${c.members_cap}`, sub: `เหลือ ${remaining} ที่` }].map((s, i) => (
              <div key={i} className="cd-hero-stat" style={{ background: PS.ink }}>
                <div style={{ fontSize: 9.5, color: 'rgba(245,241,234,0.55)', textTransform: 'uppercase', letterSpacing: 1 }}>{s.l}</div>
                <div className="num" style={{ fontSize: 17, fontWeight: 500, marginTop: 5, lineHeight: 1 }}>{s.v}</div>
                <div style={{ fontSize: 10, color: 'rgba(245,241,234,0.5)', marginTop: 4 }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {c.is_demo && (
          <div data-testid="demo-banner" style={{ margin: '14px 16px 0', padding: '10px 14px', background: 'oklch(0.72 0.12 82 / 0.16)', border: '1px dashed oklch(0.62 0.12 75)', borderRadius: 12, display: 'flex', gap: 9, alignItems: 'flex-start' }}>
            <Icon.info size={15} stroke="oklch(0.46 0.12 70)" />
            <div style={{ fontSize: 11.5, color: 'oklch(0.4 0.1 65)', lineHeight: 1.5 }}><b>โหมดตัวอย่าง (Demo)</b> — วงนี้เป็นข้อมูลสาธิตให้ลองใช้งาน ไม่ใช่วงจริง อย่าโอนเงินจริง</div>
          </div>
        )}
        {c.circle_kind === 'product' && (
          <div data-testid="product-banner" style={{ margin: '18px 16px 0', borderRadius: 18, overflow: 'hidden', border: `1px solid ${PS.line}`, background: PS.surface }}>
            {c.product_image_path && (
              <img src={c.product_image_path.startsWith('http') ? c.product_image_path : `${BACKEND_URL}/api/files/${c.product_image_path}`} alt={c.product_name} style={{ width: '100%', height: 168, objectFit: 'cover', display: 'block' }} />
            )}
            <div style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: 'oklch(0.72 0.12 82 / 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon.starO size={20} stroke="oklch(0.5 0.12 70)" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10.5, color: PS.terracotta, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>แชร์ซื้อสินค้า · จับสลากผู้รับ</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2 }}>{c.product_name || c.name}</div>
                <div style={{ fontSize: 12, color: PS.textMuted, marginTop: 2 }}>{c.product_price ? thb(c.product_price) : '—'} · {c.interest_enabled ? 'เอาดอก' : 'ไม่เอาดอก (เท่ากันทุกคน)'}</div>
              </div>
            </div>
          </div>
        )}

        {/* Host card */}
        <div onClick={() => setPerson({ user_id: c.host_user_id, name: c.host_name })} data-testid="detail-host-card" className="ps-press" style={{ margin: '18px 16px', padding: 16, background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 18, cursor: 'pointer' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Avatar name={c.host_name} size={48} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{c.host_name}</div>{c.verified && <VerifiedMark size={13} />}
              </div>
              <div style={{ fontSize: 11.5, color: PS.textMuted, marginTop: 2 }}>ท้าวแชร์ · {c.area_name} · แตะดูประวัติ</div>
            </div>
            <div onClick={(e) => { e.stopPropagation(); gatedNav(`/app/circle/${id}/chat`, 'เข้าร่วมแชทกลุ่มต้องเข้าสู่ระบบก่อน'); }} className="ps-press" data-testid="detail-chat-btn" style={{ width: 36, height: 36, borderRadius: 18, border: `1px solid ${PS.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.chat size={16} stroke={PS.ink} /></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <CreditMeter score={c.host_credit || 740} size={76} />
              <div>
                <div style={{ fontSize: 11, color: PS.textMuted }}>เครดิตท้าว</div>
                <div style={{ fontSize: 12, color: PS.text, marginTop: 4, fontWeight: 600 }}>ดีมาก</div>
              </div>
            </div>
            <div style={{ borderLeft: `1px solid ${PS.line}`, paddingLeft: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div><div style={{ fontSize: 10, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>ตรงเวลา</div><div className="num" style={{ fontSize: 18 }}>{c.host_on_time || 100}%</div></div>
              <div><div style={{ fontSize: 10, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>รีวิว</div><div className="num" style={{ fontSize: 18 }}>★ {c.rating} <span style={{ fontSize: 12, color: PS.textMuted }}>({c.reviews})</span></div></div>
            </div>
          </div>
        </div>

        <CircleSchedule rounds={rounds} />

        {/* Hands board */}
        <div style={{ padding: '0 20px 10px', fontSize: 15, fontWeight: 600 }}>สถานะมือ · ใครเปียแล้วบ้าง</div>
        <div style={{ margin: '0 16px 16px' }}>
          <HandsBoard circleId={id} onSelect={setPerson} />
        </div>
        {canLeave && (
          <div style={{ margin: '0 16px 16px', textAlign: 'center' }}>
            <button data-testid="detail-leave-btn" onClick={leave} className="ps-press" style={{ background: 'transparent', border: `1px solid ${PS.line}`, borderRadius: 12, padding: '10px 18px', fontSize: 12.5, fontWeight: 600, color: PS.textMuted, fontFamily: PS.fontUI }}>ถอนตัวจากวงนี้</button>
          </div>
        )}

        {/* Trust */}
        {c.circle_kind !== 'product' && (<>
        <div style={{ padding: '0 20px 10px', fontSize: 15, fontWeight: 600 }}>ลองคำนวณดอก</div>
        <div style={{ margin: '0 16px 16px' }}>
          <DokCalculator amount={c.amount_per_round} members={c.members_cap} mode={c.dok_mode || 'tam'} />
        </div>
        </>)}

        {/* Reviews */}
        <CircleReviews reviews={reviews} fallbackRating={c.rating} fallbackReviews={c.reviews} canReview={canReview} onWrite={() => setShowReview(true)} />

        {/* Trust */}
        <div style={{ padding: '0 20px 10px', fontSize: 15, fontWeight: 600 }}>ความปลอดภัย</div>
        <div style={{ margin: '0 16px 16px', padding: 14, background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {TRUST.map((x, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ width: 24, height: 24, borderRadius: 8, background: 'oklch(0.78 0.06 155 / 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.check size={14} stroke="oklch(0.38 0.08 155)" sw={2} /></div>
              <div style={{ flex: 1 }}><div style={{ fontSize: 12.5, fontWeight: 500 }}>{x.k}</div><div style={{ fontSize: 11, color: PS.textMuted }}>{x.v}</div></div>
            </div>
          ))}
        </div>
        <div style={{ height: 90 }} />
      </div>

      {/* Sticky CTA */}
      <div style={{ borderTop: `1px solid ${PS.line}`, padding: '12px 16px', background: PS.surface2, display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10.5, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>{c.is_member ? (c.my_status === 'pending' ? 'สถานะคำขอ' : 'คุณเป็นสมาชิก') : `เหลือ ${remaining} ที่`}</div>
          <div className="num" style={{ fontSize: 18, fontWeight: 500 }}>{thb(c.amount_per_round)} <span style={{ fontSize: 12, color: PS.textMuted, fontFamily: PS.fontUI }}>/ เดือน</span></div>
        </div>
        {c.is_member && c.my_status === 'pending' ? (
          <div data-testid="detail-pending" style={{ height: 48, padding: '0 22px', background: 'oklch(0.88 0.08 80 / 0.5)', color: 'oklch(0.38 0.1 60)', borderRadius: 24, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600 }}>
            <Icon.info size={15} stroke="oklch(0.4 0.1 60)" /> รอท้าวอนุมัติ
          </div>
        ) : c.is_member ? (
          <button data-testid="detail-open-vault-btn" onClick={() => nav(`/app/circle/${id}/vault`)} className="ps-press" style={{ height: 48, padding: '0 22px', background: PS.ink, color: PS.page, borderRadius: 24, border: 'none', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, fontFamily: PS.fontUI }}>
            ดูห้องนิรภัยหลักฐาน <Icon.chevronR size={14} stroke={PS.page} />
          </button>
        ) : remaining <= 0 ? (
          <div style={{ height: 48, padding: '0 22px', background: PS.panel, color: PS.textMuted, borderRadius: 24, display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 600 }}>วงเต็มแล้ว</div>
        ) : (
          <button data-testid="detail-join-btn" onClick={() => (user ? setShowJoin(true) : promptLogin('เข้าร่วมวงแชร์ต้องเข้าสู่ระบบก่อน'))} className="ps-press" style={{ height: 48, padding: '0 22px', background: PS.ink, color: PS.page, borderRadius: 24, border: 'none', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, fontFamily: PS.fontUI }}>
            ขอเข้าร่วม <Icon.chevronR size={14} stroke={PS.page} />
          </button>
        )}
      </div>

      {showJoin && <JoinSheet circle={c} onClose={() => setShowJoin(false)} onJoined={() => reload()} />}
      {showShare && <ShareSheet circle={c} onClose={() => setShowShare(false)} />}
      {showReview && <ReviewSheet circleId={id} hostName={c.host_name} existing={reviews?.my_review} onClose={() => setShowReview(false)} onSaved={() => { reloadReviews(); reload(); }} />}
      {person && <PersonSheet circleId={id} person={person} onClose={() => setPerson(null)} />}
    </Screen>
  );
}
